import { useDispatch } from 'react-redux';
import { getUserInitials } from '../../../../utils/Initials.util';
import { WhiteButton } from '../../../ui/Button/Buttons';
import { useAppSelector } from '../../../../hooks/redux';
import { UserListItem } from '../../../../types/Team.types';
import {
  setCurrentUser,
  setSorting,
  setUsersOffset
} from '../../../../store/slices/userManagementSlice';
import { selectIsSuperAdmin } from '../../../../store/selectors/authSelectors';
import { useUserManagement } from '../../../hooks/useUserManagement';
import { formatDate } from '../../../../utils/Date.util';
import { TableHeader } from '../../../tables/AISystemsTable/components/TableHeader/TableHeader';
import { TruncatedText } from '../../../../utils/TruncatedText';
import { getPlatformRoleLabel } from '../../../../utils/Functions.utils';
import Loader from '../../../ui/Loader/Loader';

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  EXPIRED: 'Expired',
  ACCEPTED: 'Accepted'
};

const displayNameFor = (item: UserListItem) => {
  // Collapse whitespace runs, not just the ends: a stray trailing space on
  // firstName joins into a double space, which HTML renders collapsed so the
  // name looks right while breaking anything that tokenises it.
  const name = `${item.firstName ?? ''} ${item.lastName ?? ''}`
    .replace(/\s+/g, ' ')
    .trim();

  // An invitation carries no name until it is accepted.
  return name || '--';
};

export function UsersTable() {
  const dispatch = useDispatch();
  const { users, usersLoading, sorting } = useAppSelector(
    (state) => state.userManagement
  );
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const { fetchAiSystemsForUser } = useUserManagement();

  const items = users?.items ?? [];
  const hasMore = users?.hasMore ?? false;

  const handleUserClick = (item: UserListItem) => {
    const isRegistered = item.type === 'REGISTERED';

    dispatch(
      setCurrentUser({
        userId: item.userId,
        email: item.email,
        firstName: item.firstName ?? '',
        lastName: item.lastName ?? '',
        name: displayNameFor(item),
        assignedAiSystems: item.assignedAiSystems,
        isRegistered,
        role: item.platformRole,
        invitationStatus: item.invitationStatus
      })
    );

    // Only a registered member holds AI system assignments; a pending
    // invitation has none to fetch.
    if (isRegistered) {
      fetchAiSystemsForUser(item.userId);
    }
  };

  const handleSort = (sortKey: string) => {
    const newOrder =
      sorting.sortBy === sortKey && sorting.sortOrder === 'asc'
        ? 'desc'
        : 'asc';

    dispatch(setSorting({ sortBy: sortKey, sortOrder: newOrder }));
  };

  return (
    <div className="w-full">
      <div className="w-full space-y-4">
        <div className="w-full overflow-hidden rounded-lg bg-white shadow-sm border border-solid border-gray-250v2">
          <table className="w-full table-fixed">
            <TableHeader
              className="sticky top-0"
              type="RegisteredUsers"
              sortBy={sorting.sortBy}
              sortOrder={sorting.sortOrder}
              onSort={handleSort}
              // Everyone but a superadmin sees a single organization, so the
              // column would be a repeated constant.
              hiddenColumnKeys={isSuperAdmin ? [] : ['organization']}
            />
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.userId}
                  className="w-full flex flex-row cursor-pointer bg-white border-t border-gray-100 hover:bg-gray-50"
                  onClick={() => handleUserClick(item)}
                >
                  <td className="min-w-48 w-full flex py-4 px-6 gap-1 text-sm font-medium items-center text-gray-900v2">
                    <div className="flex flex-row gap-4 self-stretch items-center min-w-0">
                      <div className="flex shrink-0 rounded-[12px] w-6 h-6 bg-gray-100 justify-center items-center text-xs font-normal text-gray-800">
                        {item.type === 'REGISTERED'
                          ? getUserInitials(displayNameFor(item))
                          : '-'}
                      </div>
                      <TruncatedText
                        text={displayNameFor(item)}
                        className="text-left"
                      />
                    </div>
                  </td>
                  <td className="min-w-56 w-full flex py-4 px-6 gap-1 text-sm font-normal text-gray-550 items-center">
                    <TruncatedText text={item.email} className="text-left" />
                  </td>
                  {isSuperAdmin && (
                    <td className="min-w-40 w-full flex py-4 px-6 gap-1 text-sm font-normal text-gray-550 items-center">
                      <TruncatedText
                        text={item.organization?.name || '--'}
                        className="text-left"
                      />
                    </td>
                  )}
                  <td className="min-w-32 w-full flex py-4 px-6 gap-1 text-sm font-normal text-gray-550 items-center">
                    {getPlatformRoleLabel(item.platformRole) || '--'}
                  </td>
                  <td className="min-w-36 w-full flex py-4 px-6 gap-1 text-sm font-normal text-gray-550 items-center">
                    {item.assignedAiSystems || '--'}
                  </td>
                  <td className="min-w-32 w-full flex py-4 px-6 gap-1 text-sm font-normal text-gray-550 items-center">
                    {item.invitationStatus
                      ? STATUS_LABELS[item.invitationStatus] ||
                        item.invitationStatus
                      : 'Active'}
                  </td>
                  <td className="min-w-36 w-full flex py-4 px-6 gap-1 text-sm font-normal text-gray-550 items-center">
                    {item.lastActive ? formatDate(item.lastActive) : '--'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* The server caps a page at 100 rows, so a superadmin looking across
            every organization always needs this. */}
        {hasMore && (
          <div className="flex justify-center py-4">
            {usersLoading ? (
              <Loader />
            ) : (
              <WhiteButton
                onClick={() => dispatch(setUsersOffset(items.length))}
              >
                {`Load more (${items.length} of ${users?.total ?? 0})`}
              </WhiteButton>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
